/**
 * Centralized permission system for AirPlx application
 *
 * This module consolidates all permission logic that was previously scattered
 * across multiple components. It provides type-safe role and permission checking
 * with a single source of truth for all authorization decisions.
 */

export enum UserRole {
  SYSTEM_ADMIN = "system_admin",
  FBO_ADMIN = "fbo_admin",
  FBO_MANAGER = "manager",
  FBO_OPERATOR = "operator",
  CSR = "csr",
  AIRPLX_EMPLOYEE = "airplx_employee",
}

/**
 * Display names for user roles
 * Maps backend role values to user-friendly display names
 */
export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  fbo_admin: "FBO Admin",
  manager: "Manager",
  operator: "Operator",
  csr: "CSR",
};

/**
 * Format a role value to its display name
 * @param role - The backend role value (e.g., "fbo_admin")
 * @returns The formatted display name (e.g., "FBO Admin")
 */
export const formatRoleName = (role: string): string => {
  return ROLE_DISPLAY_NAMES[role] || role;
};

/**
 * Standard role options for dropdowns
 * Ordered by permission level: FBO Admin > Manager > Operator > CSR
 */
export const ROLE_OPTIONS = [
  { value: "fbo_admin", label: "FBO Admin" },
  { value: "manager", label: "Manager" },
  { value: "operator", label: "Operator" },
  { value: "csr", label: "CSR" },
];

export enum Permission {
  // System-level permissions
  ACCESS_SUPER_ADMIN = "access_super_admin",

  // FBO-level permissions
  MODIFY_AIRCRAFT_PREFERENCES = "modify_aircraft_preferences",
  MODIFY_LAYOUT = "modify_layout",
  MANAGE_TEAM_MEMBERS = "manage_team_members",
  MODIFY_TENANTS = "modify_tenants",
  USE_FAVORITES = "use_favorites",
  CREATE_TRIPS = "create_trips",
  DELETE_TRIPS = "delete_trips",

  // NEW: Backend RLS permissions
  MODIFY_RATES = "modify_rates",
  MODIFY_INFRASTRUCTURE = "modify_infrastructure",
  MODIFY_CONFIGURATION = "modify_configuration",

  // View permissions
  VIEW_SCHEDULE = "view_schedule",
  VIEW_LAYOUT = "view_layout",
}

export interface PermissionContext {
  user: {
    isAdmin: boolean;
    userid: string;
  };
  fbo: {
    id: string;
    user_role: string;
    settings: {
      viewOnlyForFBOAdmin: boolean;
      viewOnlyForManager: boolean;
      viewOnlyForOperator: boolean;
      viewOnlyForCSR: boolean;
      modifyTenantsForFBOAdmin: boolean;
      modifyTenantsForManager: boolean;
      modifyTenantsForOperator: boolean;
      modifyTenantsForCSR: boolean;
      addTeamMembersForFBOAdmin: boolean;
      addTeamMembersForManager: boolean;
      addTeamMembersForOperator: boolean;
      addTeamMembersForCSR: boolean;
      useFavoritesForFBOAdmin: boolean;
      useFavoritesForManager: boolean;
      useFavoritesForOperator: boolean;
      useFavoritesForCSR: boolean;
      modifyLayoutForFBOAdmin: boolean;
      modifyLayoutForManager: boolean;
      modifyLayoutForOperator: boolean;
      modifyLayoutForCSR: boolean;
      createTripsForFBOAdmin: boolean;
      createTripsForManager: boolean;
      createTripsForOperator: boolean;
      createTripsForCSR: boolean;
      deleteTripsForFBOAdmin: boolean;
      deleteTripsForManager: boolean;
      deleteTripsForOperator: boolean;
      deleteTripsForCSR: boolean;
      modifyRatesForFBOAdmin: boolean;
      modifyRatesForManager: boolean;
      modifyRatesForOperator: boolean;
      modifyRatesForCSR: boolean;
      modifyInfrastructureForFBOAdmin: boolean;
      modifyInfrastructureForManager: boolean;
      modifyInfrastructureForOperator: boolean;
      modifyInfrastructureForCSR: boolean;
      modifyConfigurationForFBOAdmin: boolean;
      modifyConfigurationForManager: boolean;
      modifyConfigurationForOperator: boolean;
      modifyConfigurationForCSR: boolean;
    };
  } | null;
  /** Flag indicating if admin role override is active (for testing) */
  roleOverrideActive?: boolean;
}

/**
 * Determine the user's role based on their admin status and FBO relationship
 */
export function getUserRole(user: any, fbo: any, roleOverrideActive: boolean = false): UserRole {
  // When role override is active, use the FBO role (not admin status)
  if (!roleOverrideActive && (user.isadmin || user.isAdmin)) {
    return UserRole.SYSTEM_ADMIN;
  }

  if (fbo?.user_role === "fbo_admin") {
    return UserRole.FBO_ADMIN;
  }

  if (fbo?.user_role === "manager") {
    return UserRole.FBO_MANAGER;
  }

  if (fbo?.user_role === "csr") {
    return UserRole.CSR;
  }

  return UserRole.FBO_OPERATOR;
}

/**
 * Check if a user has a specific permission given their context
 */
export function hasPermission(
  permission: Permission,
  context: PermissionContext
): boolean {
  // Pass roleOverrideActive flag to getUserRole so it uses FBO role instead of admin status
  const userRole = getUserRole(context.user, context.fbo, context.roleOverrideActive);

  // System admins have all permissions
  // EXCEPT when role override is active (for testing) - then respect the overridden role
  if (userRole === UserRole.SYSTEM_ADMIN && !context.roleOverrideActive) {
    return true;
  }

  // No FBO context means no FBO permissions (except system permissions)
  if (!context.fbo) {
    return permission === Permission.ACCESS_SUPER_ADMIN ? false : false;
  }

  switch (permission) {
    case Permission.ACCESS_SUPER_ADMIN:
      // System admins already returned early (line 141), so this is always false
      return false;

    case Permission.MODIFY_AIRCRAFT_PREFERENCES:
      return !isInViewOnlyMode(context);

    case Permission.MODIFY_LAYOUT:
      if (userRole === UserRole.FBO_ADMIN) {
        // Fallback to Manager setting if FBOAdmin setting is undefined
        return (
          (context.fbo.settings.modifyLayoutForFBOAdmin ??
            context.fbo.settings.modifyLayoutForManager ??
            true) &&
          !isInViewOnlyMode(context)
        );
      }
      if (userRole === UserRole.FBO_MANAGER) {
        return (
          context.fbo.settings.modifyLayoutForManager &&
          !isInViewOnlyMode(context)
        );
      }
      if (userRole === UserRole.FBO_OPERATOR) {
        return (
          context.fbo.settings.modifyLayoutForOperator &&
          !isInViewOnlyMode(context)
        );
      }
      if (userRole === UserRole.CSR) {
        return (
          context.fbo.settings.modifyLayoutForCSR && !isInViewOnlyMode(context)
        );
      }
      return !isInViewOnlyMode(context);

    case Permission.MANAGE_TEAM_MEMBERS:
      if (userRole === UserRole.FBO_ADMIN) {
        // Fallback to Manager setting if FBOAdmin setting is undefined
        return (
          context.fbo.settings.addTeamMembersForFBOAdmin ??
          context.fbo.settings.addTeamMembersForManager ??
          true
        );
      }
      if (userRole === UserRole.FBO_MANAGER) {
        return context.fbo.settings.addTeamMembersForManager;
      }
      if (userRole === UserRole.FBO_OPERATOR) {
        return context.fbo.settings.addTeamMembersForOperator;
      }
      if (userRole === UserRole.CSR) {
        return context.fbo.settings.addTeamMembersForCSR;
      }
      return false;

    case Permission.MODIFY_TENANTS:
      if (userRole === UserRole.FBO_ADMIN) {
        // Fallback to Manager setting if FBOAdmin setting is undefined
        return (
          context.fbo.settings.modifyTenantsForFBOAdmin ??
          context.fbo.settings.modifyTenantsForManager ??
          true
        );
      }
      if (userRole === UserRole.FBO_MANAGER) {
        return context.fbo.settings.modifyTenantsForManager;
      }
      if (userRole === UserRole.FBO_OPERATOR) {
        return context.fbo.settings.modifyTenantsForOperator;
      }
      if (userRole === UserRole.CSR) {
        return context.fbo.settings.modifyTenantsForCSR;
      }
      return false;

    case Permission.USE_FAVORITES:
      if (userRole === UserRole.FBO_ADMIN) {
        // Fallback to Manager setting if FBOAdmin setting is undefined
        return (
          context.fbo.settings.useFavoritesForFBOAdmin ??
          context.fbo.settings.useFavoritesForManager ??
          true
        );
      }
      if (userRole === UserRole.FBO_MANAGER) {
        return context.fbo.settings.useFavoritesForManager;
      }
      if (userRole === UserRole.FBO_OPERATOR) {
        return context.fbo.settings.useFavoritesForOperator;
      }
      if (userRole === UserRole.CSR) {
        return context.fbo.settings.useFavoritesForCSR;
      }
      return false;

    case Permission.VIEW_SCHEDULE:
      return true; // All authenticated users can view schedules

    case Permission.VIEW_LAYOUT:
      return true; // All authenticated users can view layouts

    case Permission.CREATE_TRIPS:
      if (userRole === UserRole.FBO_ADMIN) {
        // Fallback to Manager setting if FBOAdmin setting is undefined
        return (
          (context.fbo.settings.createTripsForFBOAdmin ??
            context.fbo.settings.createTripsForManager ??
            true) &&
          !isInViewOnlyMode(context)
        );
      }
      if (userRole === UserRole.FBO_MANAGER) {
        return (
          context.fbo.settings.createTripsForManager &&
          !isInViewOnlyMode(context)
        );
      }
      if (userRole === UserRole.FBO_OPERATOR) {
        return (
          context.fbo.settings.createTripsForOperator &&
          !isInViewOnlyMode(context)
        );
      }
      if (userRole === UserRole.CSR) {
        return (
          context.fbo.settings.createTripsForCSR && !isInViewOnlyMode(context)
        );
      }
      return !isInViewOnlyMode(context);

    case Permission.DELETE_TRIPS:
      // Note: System admins already returned early (line 141)
      if (userRole === UserRole.FBO_ADMIN) {
        // Fallback to Manager setting if FBOAdmin setting is undefined
        return (
          (context.fbo.settings.deleteTripsForFBOAdmin ??
            context.fbo.settings.deleteTripsForManager ??
            true) &&
          !isInViewOnlyMode(context)
        );
      }
      if (userRole === UserRole.FBO_MANAGER) {
        return (
          context.fbo.settings.deleteTripsForManager &&
          !isInViewOnlyMode(context)
        );
      }
      if (userRole === UserRole.FBO_OPERATOR) {
        return (
          context.fbo.settings.deleteTripsForOperator &&
          !isInViewOnlyMode(context)
        );
      }
      if (userRole === UserRole.CSR) {
        return (
          context.fbo.settings.deleteTripsForCSR && !isInViewOnlyMode(context)
        );
      }
      return !isInViewOnlyMode(context);

    case Permission.MODIFY_RATES:
      if (userRole === UserRole.FBO_ADMIN) {
        return (
          context.fbo.settings.modifyRatesForFBOAdmin ??
          context.fbo.settings.modifyRatesForManager ??
          true
        );
      }
      if (userRole === UserRole.FBO_MANAGER) {
        return context.fbo.settings.modifyRatesForManager ?? true;
      }
      if (userRole === UserRole.FBO_OPERATOR) {
        return context.fbo.settings.modifyRatesForOperator ?? false;
      }
      if (userRole === UserRole.CSR) {
        return context.fbo.settings.modifyRatesForCSR ?? false;
      }
      return false;

    case Permission.MODIFY_INFRASTRUCTURE:
      if (userRole === UserRole.FBO_ADMIN) {
        return (
          context.fbo.settings.modifyInfrastructureForFBOAdmin ??
          context.fbo.settings.modifyInfrastructureForManager ??
          true
        );
      }
      if (userRole === UserRole.FBO_MANAGER) {
        return context.fbo.settings.modifyInfrastructureForManager ?? true;
      }
      if (userRole === UserRole.FBO_OPERATOR) {
        return context.fbo.settings.modifyInfrastructureForOperator ?? false;
      }
      if (userRole === UserRole.CSR) {
        return context.fbo.settings.modifyInfrastructureForCSR ?? false;
      }
      return false;

    case Permission.MODIFY_CONFIGURATION:
      if (userRole === UserRole.FBO_ADMIN) {
        return (
          context.fbo.settings.modifyConfigurationForFBOAdmin ??
          context.fbo.settings.modifyConfigurationForManager ??
          true
        );
      }
      if (userRole === UserRole.FBO_MANAGER) {
        return context.fbo.settings.modifyConfigurationForManager ?? true;
      }
      if (userRole === UserRole.FBO_OPERATOR) {
        return context.fbo.settings.modifyConfigurationForOperator ?? false;
      }
      if (userRole === UserRole.CSR) {
        return context.fbo.settings.modifyConfigurationForCSR ?? false;
      }
      return false;

    default:
      return false;
  }
}

/**
 * INTERNAL HELPER: Check if the user is in view-only mode based on their role and FBO settings
 *
 * This function is used internally by hasPermission() to enforce view-only mode.
 * Components should NOT call this directly - use hasPermission() instead.
 */
function isInViewOnlyMode(context: PermissionContext): boolean {
  const userRole = getUserRole(context.user, context.fbo);

  if (userRole === UserRole.SYSTEM_ADMIN) {
    return false;
  }

  if (!context.fbo) {
    return true;
  }

  if (userRole === UserRole.FBO_ADMIN) {
    return (
      context.fbo.settings.viewOnlyForFBOAdmin ??
      context.fbo.settings.viewOnlyForManager ??
      false
    );
  }

  if (userRole === UserRole.FBO_MANAGER) {
    return context.fbo.settings.viewOnlyForManager;
  }

  if (userRole === UserRole.CSR) {
    return context.fbo.settings.viewOnlyForCSR;
  }

  return context.fbo.settings.viewOnlyForOperator;
}

/**
 * @deprecated Use hasPermission(Permission.X) with usePermissions() hook instead.
 * This function is kept for backwards compatibility with display-only settings.
 *
 * @example
 * // OLD (deprecated):
 * const setting = isManagerLevel(activeFBO)
 *   ? activeFBO.settings.viewOnlyForManager
 *   : activeFBO.settings.viewOnlyForOperator;
 *
 * // NEW (recommended):
 * const { hasPermission } = usePermissions();
 * const canEdit = hasPermission(Permission.MODIFY_LAYOUT);
 */
export function isManagerLevel(
  fbo: { user_role: string } | null | undefined
): boolean {
  return fbo?.user_role === "manager" || fbo?.user_role === "fbo_admin";
}

/**
 * Get role-specific setting value with proper fallback handling
 *
 * NOTE: This function should ONLY be used for display-only settings (e.g., displayEstimatedValue).
 * For permission checks that control data mutations, use hasPermission(Permission.X) instead.
 *
 * @param fbo - FBO object with user_role and settings
 * @param settingName - Base name of the setting (e.g., "displayEstimatedValue")
 * @param defaultValue - Default value if all settings are undefined
 * @returns The appropriate setting value for the user's role
 *
 * @example
 * // For display-only settings (CORRECT):
 * const displayEstimatedValue = getRoleSetting(activeFBO, 'displayEstimatedValue', true);
 *
 * // For permission checks (USE hasPermission instead):
 * const { hasPermission } = usePermissions();
 * const canEdit = hasPermission(Permission.MODIFY_LAYOUT);
 */
export function getRoleSetting<T>(
  fbo: { user_role: string; settings?: any } | null | undefined,
  settingName: string,
  defaultValue: T
): T {
  if (!fbo?.settings) return defaultValue;

  const role = fbo.user_role;
  const settings = fbo.settings;

  // Try role-specific setting first
  if (role === "fbo_admin") {
    return (
      settings[`${settingName}ForFBOAdmin`] ??
      settings[`${settingName}ForManager`] ??
      defaultValue
    );
  }
  if (role === "manager") {
    return settings[`${settingName}ForManager`] ?? defaultValue;
  }
  if (role === "csr") {
    return settings[`${settingName}ForCSR`] ?? defaultValue;
  }
  // operator or unknown role
  return settings[`${settingName}ForOperator`] ?? defaultValue;
}
